/**
 * Iter 118 — DPR refactor to match user's Excel template (No-RA-Bill version).
 *
 * 5-step form:
 *  1. Context         — date, project, WO, PM, contract value, report no, weather
 *  2. Manpower        — counts + performance + labour category split
 *  3. Activities & Materials  — BOQ activities, today consumption, next-day procurement, store ledger
 *  4. Site Status     — work fronts, work holds, rework, hindrances
 *  5. Quality/Safety/P&M/Photos — safety + QC checklists, housekeeping, plant&machinery, photos, sign-off
 *
 * Exports both <DPR /> (page) and <DPRForm /> (used by mobile page).
 */
import React, { useEffect, useState } from 'react';
import { Plus, Camera, MapPin, X, CloudSun, Cloud, CloudRain, Sun, Users, ClipboardList, AlertTriangle, RefreshCw, Hammer, ShieldCheck, Boxes, Truck, Pause, FileWarning } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPost, api } from '../lib/api';
import { enqueue, cachedGet, totalQueuedPhotos, MAX_OFFLINE_PHOTOS, PHOTO_WARN_THRESHOLD } from '../lib/offlineQueue';
import { formatDate } from '../lib/format';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useFormFieldHelpers } from '../hooks/useFormFieldHelpers';

const WEATHER = [
  { v: 'Sunny', icon: Sun },
  { v: 'Cloudy', icon: Cloud },
  { v: 'Rainy', icon: CloudRain },
  { v: 'Foggy', icon: CloudSun },
];

const MANPOWER_CATEGORIES = [
  { k: 'engineers', label: 'Engineers' },
  { k: 'supervisors', label: 'Supervisors' },
  { k: 'safety_officer', label: 'Safety Officer' },
  { k: 'qa_qc', label: 'QA / QC' },
  { k: 'skilled', label: 'Skilled Labour' },
  { k: 'unskilled', label: 'Unskilled Labour' },
  { k: 'operators', label: 'Operators' },
];

const PERF_OPTS = ['—', 'Excellent', 'Good', 'Average', 'Poor'];

const DEFAULT_SAFETY_CHECKS = [
  { parameter: 'Toolbox Talk', status: '', details: '' },
  { parameter: 'PPE Compliance', status: '', details: '' },
  { parameter: 'Work at Height', status: '', details: '' },
  { parameter: 'Electrical Safety', status: '', details: '' },
];

const DEFAULT_QC_CHECKS = [
  { activity: 'Concrete Pour', status: '', remarks: '' },
  { activity: 'Rebar Inspection', status: '', remarks: '' },
  { activity: 'Shuttering Check', status: '', remarks: '' },
];

const EMPTY_FORM = {
  date: new Date().toISOString().slice(0, 10),
  weather: 'Sunny',
  project_id: '', project_name: '',
  work_order_id: '', work_order_code: '',
  report_no: '',
  project_manager: '',
  contract_value_cr: '',
  weather_impact_hours: 0,
  weather_temperature_c: '',
  weather_rainfall_mm: '',
  weather_wind: '',
  weather_humidity: '',
  weather_impact_flag: 'No',
  weather_remarks: '',
  manpower_count: { engineers: 0, supervisors: 0, safety_officer: 0, qa_qc: 0, skilled: 0, unskilled: 0, operators: 0 },
  manpower_performance: { engineers: '', supervisors: '', safety_officer: '', qa_qc: '', skilled: '', unskilled: '', operators: '' },
  labour_category_split: { skilled: 0, semi_skilled: 0, unskilled: 0, supervisor: 0 },
  activities: [],
  activity_description: '',
  progress_note: '',
  materials_consumed: [],
  procurement_next_day: [],
  store_consumption: [],
  boq_consumption: [],
  equipment_running: [],
  plant_machinery: [],
  work_holds: [],
  work_fronts: [],
  rework_items: [],
  hindrances: [],
  safety_checks: DEFAULT_SAFETY_CHECKS,
  qc_checks: DEFAULT_QC_CHECKS,
  housekeeping_checks: [],
  issues: '',
  safety_incidents: '',
  rfis_raised: '',
  supervisor_signoff: '',
  submitted_by: '',
};

export default function DPR() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [filterProject, setFilterProject] = useState('');
  const [filterWO, setFilterWO] = useState('');
  const [projects, setProjects] = useState([]);

  const load = async () => {
    try { setItems(await apiGet('/dpr')); } catch {}
    try { setProjects(await apiGet('/projects')); } catch {}
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setFilterWO(''); }, [filterProject]);

  const filtered = items.filter(i =>
    (!filterProject || i.project_id === filterProject) &&
    (!filterWO || i.work_order_id === filterWO)
  );

  const selectedProject = projects.find(p => p.id === filterProject);
  const workOrders = selectedProject?.work_orders || [];

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <PageHeader
        overline="Daily Progress · Single Sheet"
        title="DPR Log"
        subtitle="Site progress aligned to the EEPC DPR template. Captures manpower, materials, work fronts, safety, QC, plant & hindrances. Pushes completed quantities to BOQ executed."
        actions={
          <button data-testid="dpr-new-btn" onClick={()=>setOpen(true)} className="flex items-center gap-2 px-4 py-2.5 bg-[#3b4872] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#2c3658] hover:-translate-y-px transition-all">
            <Plus size={15} /> Submit DPR
          </button>
        }
      />

      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 flex items-center gap-3 flex-wrap">
        <span className="kpi-overline">Filter</span>
        <select value={filterProject} onChange={e => setFilterProject(e.target.value)} data-testid="dpr-filter-project"
          className="px-3 py-1.5 border border-slate-300 rounded-sm text-sm bg-white">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{`${p.code} · ${p.name}`}</option>)}
        </select>
        <select value={filterWO} onChange={e => setFilterWO(e.target.value)} data-testid="dpr-filter-wo"
          disabled={workOrders.length === 0}
          className="px-3 py-1.5 border border-slate-300 rounded-sm text-sm bg-white disabled:opacity-50 disabled:cursor-not-allowed">
          <option value="">All Work Orders</option>
          {workOrders.map(w => <option key={w.id} value={w.id}>{w.code}{w.title ? ` · ${w.title.slice(0, 30)}` : ''}</option>)}
        </select>
        {(filterProject || filterWO) && (
          <span className="text-[11px] text-slate-500 ml-auto">
            Showing <b>{filtered.length}</b> of {items.length} DPRs
          </span>
        )}
      </div>

      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-sm">
            <div className="text-xs uppercase tracking-widest font-display text-slate-400 mb-2">No DPRs submitted</div>
            <button onClick={()=>setOpen(true)} className="text-[#3b4872] text-sm font-display font-semibold">Submit the first DPR</button>
          </div>
        )}
        {filtered.map(d => <DPRCard key={d.id} dpr={d} onChanged={load} />)}
      </div>

      {open && <DPRForm projects={projects} onClose={()=>setOpen(false)} onSaved={()=>{ setOpen(false); load(); }} />}
    </div>
  );
}

function DPRCard({ dpr, onChanged }) {
  const [busy, setBusy] = useState(false);
  const syncBoq = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/dpr/${dpr.id}/sync-boq`);
      toast.success(`BOQ synced: ${r.data.updated_items} items updated`);
      onChanged();
    } catch { toast.error('Sync failed'); }
    setBusy(false);
  };

  const mc = dpr.manpower_count || {};
  const mcTotal = MANPOWER_CATEGORIES.reduce((s, c) => s + (Number(mc[c.k]) || 0), 0);

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5 hover:border-[#3b4872]/40">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="kpi-overline">{formatDate(dpr.date)}{dpr.report_no ? ` · Report ${dpr.report_no}` : ''}</div>
          <h3 className="font-display font-bold text-lg mt-1">{dpr.project_name || dpr.project}</h3>
          <div className="flex items-center gap-3 text-xs text-slate-500 mt-1 flex-wrap">
            {dpr.weather && <span className="flex items-center gap-1"><CloudSun size={12} />{dpr.weather}</span>}
            <span><Users size={12} className="inline" /> Manpower: <b className="text-[#0F172A]">{mcTotal || dpr.manpower || dpr.total_mandays || 0}</b></span>
            {dpr.weather_impact_hours > 0 && <span className="text-amber-700">⚠ {dpr.weather_impact_hours}h lost</span>}
            <span>By: <b className="text-[#0F172A]">{dpr.submitted_by}</b></span>
            {dpr.boq_synced_at && <span className="text-emerald-700 font-display font-bold">✓ BOQ synced</span>}
          </div>
          {dpr.gps && <div className="flex items-center gap-1 text-[10px] text-slate-500 font-mono mt-1"><MapPin size={10} />{dpr.gps}</div>}
        </div>
        <div className="flex items-center gap-2">
          {dpr.photos?.length > 0 && <span className="tag bg-slate-100 text-slate-700 border border-slate-200 flex items-center gap-1"><Camera size={10} />{dpr.photos.length}</span>}
          {dpr.activities?.length > 0 && !dpr.boq_synced_at && (
            <button onClick={syncBoq} disabled={busy} data-testid={`dpr-sync-${dpr.id}`}
              className="px-3 py-1.5 text-[11px] bg-emerald-600 text-white rounded-sm font-display font-semibold flex items-center gap-1 disabled:opacity-50">
              <RefreshCw size={11} className={busy ? 'animate-spin' : ''} /> Push to BOQ
            </button>
          )}
        </div>
      </div>

      {mcTotal > 0 && (
        <div className="bg-slate-50 border-l-2 border-l-[#3b4872] p-3 text-xs mb-2 flex flex-wrap gap-3">
          <div className="kpi-overline w-full mb-1 text-[#3b4872]">Manpower</div>
          {MANPOWER_CATEGORIES.map(c => (Number(mc[c.k]) || 0) > 0 ? (
            <span key={c.k}>{c.label}: <b className="tabular-nums">{mc[c.k]}</b></span>
          ) : null)}
        </div>
      )}

      {dpr.activities?.length > 0 && (
        <div className="bg-slate-50 border-l-2 border-l-[#3b4872] p-3 text-xs mb-2">
          <div className="kpi-overline mb-1.5 text-[#3b4872]">Activities · {dpr.activities.length}</div>
          <div className="space-y-1">
            {dpr.activities.map((a, i) => (
              <div key={i} className="flex items-center justify-between gap-2 py-0.5">
                <span className="truncate flex-1">{a.activity || a.boq_code}</span>
                <span className="font-display font-bold tabular-nums">{a.qty_done} {a.unit || ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {dpr.work_holds?.length > 0 && (
        <div className="bg-rose-50 border-l-2 border-l-rose-500 p-3 text-xs mb-2">
          <div className="kpi-overline mb-1 text-rose-700"><Pause size={11} className="inline" /> Work Hold · {dpr.work_holds.length}</div>
          {dpr.work_holds.map((h, i) => (<div key={i}>• {h.ref || h.email_no} — {h.client || ''} {h.reason ? `· ${h.reason}` : ''}</div>))}
        </div>
      )}

      {dpr.work_fronts?.length > 0 && (
        <div className="bg-slate-50 border-l-2 border-l-slate-400 p-3 text-xs mb-2">
          <div className="kpi-overline mb-1 text-slate-700">Work Fronts · {dpr.work_fronts.length}</div>
          {dpr.work_fronts.map((f, i) => (<div key={i}>• {f.area} {f.activity ? `· ${f.activity}` : ''} — {f.available || '?'}</div>))}
        </div>
      )}

      {dpr.rework_items?.length > 0 && (
        <div className="bg-orange-50 border-l-2 border-l-orange-500 p-3 text-xs mb-2">
          <div className="kpi-overline mb-1 text-orange-700"><FileWarning size={11} className="inline" /> Rework · {dpr.rework_items.length}</div>
          {dpr.rework_items.map((r, i) => (<div key={i}>• {r.location || ''} — {r.reason || ''} [{r.responsibility || '—'}]</div>))}
        </div>
      )}

      {dpr.hindrances?.length > 0 && (
        <div className="bg-amber-50 border-l-2 border-l-amber-500 p-3 text-xs mb-2">
          <div className="kpi-overline mb-1 text-amber-700"><AlertTriangle size={11} className="inline" /> Hindrances · {dpr.hindrances.length}</div>
          <div className="space-y-0.5">
            {dpr.hindrances.map((h, i) => (
              <div key={i} className="flex gap-2">
                <span className="font-display font-semibold text-amber-800">[{h.category}]</span>
                <span className="flex-1">{h.description}</span>
                {h.impact_hours > 0 && <span className="text-amber-700 tabular-nums">{h.impact_hours}h</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {dpr.plant_machinery?.length > 0 && (
        <div className="bg-slate-50 border-l-2 border-l-slate-400 p-3 text-xs mb-2">
          <div className="kpi-overline mb-1 text-slate-700"><Truck size={11} className="inline" /> Plant & Machinery · {dpr.plant_machinery.length}</div>
          {dpr.plant_machinery.map((p, i) => (
            <div key={i}>• {p.equipment} {p.deployed ? `(${p.deployed} dep / ${p.required || '?'} req)` : ''} {p.working_hours ? `· ${p.working_hours}h working` : ''}{p.idle_hours ? ` · ${p.idle_hours}h idle` : ''}</div>
          ))}
        </div>
      )}

      {dpr.labour_category_split && (dpr.labour_category_split.category_total > 0) && (
        <div className="bg-slate-50 border-l-2 border-l-slate-400 p-3 text-xs mb-2">
          <div className="kpi-overline mb-1 text-slate-700">Labour · category</div>
          <div className="flex flex-wrap gap-3">
            <span>Skilled: <b className="tabular-nums">{dpr.labour_category_split.skilled}</b></span>
            <span>Semi: <b className="tabular-nums">{dpr.labour_category_split.semi_skilled}</b></span>
            <span>Unskilled: <b className="tabular-nums">{dpr.labour_category_split.unskilled}</b></span>
            <span>Supervisor: <b className="tabular-nums">{dpr.labour_category_split.supervisor}</b></span>
            <span className="text-slate-400">· Attendance total: <b className="tabular-nums text-[#0F172A]">{dpr.labour_category_split.attendance_total}</b></span>
            {typeof dpr.labour_category_split.variance === 'number' && dpr.labour_category_split.variance !== 0 && (
              <span className={Math.abs(dpr.labour_category_split.variance) <= 2 ? 'text-amber-700' : 'text-rose-700'}>
                · Variance {dpr.labour_category_split.variance > 0 ? '+' : ''}{dpr.labour_category_split.variance}
              </span>
            )}
          </div>
        </div>
      )}

      {(dpr.activity_description || dpr.progress_note) && (
        <div className="bg-slate-50 border-l-2 border-l-[#3b4872] p-3 text-sm leading-relaxed mb-2">
          <div className="kpi-overline mb-1 text-[#3b4872]">Work Done</div>
          {dpr.activity_description || dpr.progress_note}
        </div>
      )}

      {dpr.safety_incidents && dpr.safety_incidents !== 'None' && (
        <div className="bg-red-50 border-l-2 border-l-red-500 p-3 text-sm mb-2">
          <div className="kpi-overline mb-1 text-red-700"><AlertTriangle size={11} className="inline" /> Safety</div>
          {dpr.safety_incidents}
        </div>
      )}

      {dpr.photos?.length > 0 && (
        <div className="flex gap-2 mt-3 overflow-x-auto">
          {dpr.photos.map(p => <PhotoThumb key={p.id} fileId={p.id} />)}
        </div>
      )}

      {dpr.supervisor_signoff && (
        <div className="text-[10px] uppercase tracking-widest font-display font-bold text-emerald-700 mt-2">✓ Signed off by {dpr.supervisor_signoff}</div>
      )}
    </div>
  );
}

/* ============================================================ */
/*  DPRForm — used by both desktop modal and mobile fullscreen   */
/* ============================================================ */
export const DPRForm = ({ projects, onClose, onSaved, fullscreen = false, enableOffline = false }) => {
  const { user } = useAuth();
  const ff = useFormFieldHelpers('dpr_create');
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...EMPTY_FORM, submitted_by: user?.name || '' });
  const [gps, setGps] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [boqItems, setBoqItems] = useState([]);
  const [prefill, setPrefill] = useState(null);
  const [labourPreview, setLabourPreview] = useState(null);

  useEffect(() => {
    if (!form.project_id || !form.date) return;
    (async () => {
      try {
        const p = await apiGet('/dpr-prefill', { project_id: form.project_id, date: form.date });
        setPrefill(p);
        setForm(f => ({ ...f, project_name: p.project_name, equipment_running: p.equipment_running || [] }));
      } catch {}
      // Prefill Project Manager from the project record (frontend/src/pages/DPR.legacy.jsx:565-568
      // was previously disconnected free text) — keep it editable after prefilling.
      const proj = projects.find(p => p.id === form.project_id);
      if (proj?.pm) setForm(f => (f.project_manager ? f : { ...f, project_manager: proj.pm }));
      try {
        const bq = enableOffline
          ? (await cachedGet(`boq:${form.project_id}`, `/boq/by-project/${form.project_id}`)).data
          : await apiGet(`/boq/by-project/${form.project_id}`);
        setBoqItems((bq.items || []).filter(i => !i.is_group));
      } catch {}
      try {
        const lp = await apiGet('/dpr/labour-preview', { project_id: form.project_id, date: form.date });
        setLabourPreview(lp);
        setForm(f => ({
          ...f,
          labour_category_split: {
            skilled: lp.auto_category_split?.skilled || 0,
            semi_skilled: lp.auto_category_split?.semi_skilled || 0,
            unskilled: lp.auto_category_split?.unskilled || 0,
            supervisor: lp.auto_category_split?.supervisor || 0,
          },
        }));
      } catch {}
    })();
  }, [form.project_id, form.date]);

  const getGps = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not available'); return; }
    navigator.geolocation.getCurrentPosition(
      pos => { setGps(`${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`); toast.success('Location captured'); },
      () => toast.error('Failed to get location'),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const offline = enableOffline && typeof navigator !== 'undefined' && !navigator.onLine;
    if (offline) {
      // No network — compress to base64 and hold for upload on sync. Enforce a
      // hard cap across the whole queue so we never exhaust device storage.
      let queued = 0;
      try { queued = await totalQueuedPhotos(); } catch {}
      const offlineInForm = photos.filter(p => p.offline).length;
      let used = queued + offlineInForm;
      for (const f of files) {
        if (used >= MAX_OFFLINE_PHOTOS) {
          toast.error(`Offline photo limit reached (${MAX_OFFLINE_PHOTOS}). Sync pending entries to free space.`);
          break;
        }
        try {
          const dataUrl = await compressImage(f);
          setPhotos(p => [...p, { name: f.name, dataUrl, offline: true }]);
          used++;
        } catch { toast.error('Could not process ' + f.name); }
      }
      if (used >= PHOTO_WARN_THRESHOLD && used < MAX_OFFLINE_PHOTOS) {
        toast.warning(`${used} of ${MAX_OFFLINE_PHOTOS} offline photo slots used.`);
      }
    } else {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        try {
          const r = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          setPhotos(p => [...p, { id: r.data.id, name: r.data.original_filename }]);
        } catch { toast.error('Upload failed for ' + f.name); }
      }
    }
    setUploading(false);
  };

  const addActivity = () => setForm(f => ({ ...f, activities: [...f.activities, { boq_id: '', boq_code: '', activity: '', qty_done: 0, unit: '', remarks: '' }] }));
  const removeActivity = (i) => setForm(f => ({ ...f, activities: f.activities.filter((_, idx) => idx !== i) }));
  const updateActivity = (i, key, val) => setForm(f => ({ ...f, activities: f.activities.map((a, idx) => idx === i ? { ...a, [key]: val } : a) }));
  const pickBoq = (i, boq_id) => {
    const b = boqItems.find(x => x.id === boq_id);
    if (!b) return;
    updateActivity(i, 'boq_id', b.id);
    updateActivity(i, 'boq_code', b.item_code);
    updateActivity(i, 'activity', b.description?.slice(0, 100));
    updateActivity(i, 'unit', b.unit);
  };

  const arrAdd = (k, row) => setForm(f => ({ ...f, [k]: [...(f[k] || []), row] }));
  const arrRemove = (k, i) => setForm(f => ({ ...f, [k]: (f[k] || []).filter((_, idx) => idx !== i) }));
  const arrUpdate = (k, i, key, val) => setForm(f => ({ ...f, [k]: (f[k] || []).map((r, idx) => idx === i ? { ...r, [key]: val } : r) }));

  const setMpCount = (k, v) => setForm(f => ({ ...f, manpower_count: { ...f.manpower_count, [k]: Math.max(0, Number(v) || 0) } }));
  const setMpPerf = (k, v) => setForm(f => ({ ...f, manpower_performance: { ...f.manpower_performance, [k]: v } }));
  const mpTotal = MANPOWER_CATEGORIES.reduce((s, c) => s + (Number(form.manpower_count[c.k]) || 0), 0);

  const setCat = (key, val) => setForm(f => ({
    ...f,
    labour_category_split: { ...f.labour_category_split, [key]: Math.max(0, Number(val) || 0) },
  }));
  const catTotal = (form.labour_category_split.skilled || 0) +
                   (form.labour_category_split.semi_skilled || 0) +
                   (form.labour_category_split.unskilled || 0);
  const attendanceTotal = labourPreview?.total_present_from_attendance || 0;
  const variance = catTotal - attendanceTotal;

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!form.project_id) { toast.error('Select a project'); return; }
    setSubmitting(true);
    const project_name = form.project_name || projects.find(p => p.id === form.project_id)?.name || '';
    const basePayload = {
      ...form,
      project_name,
      manpower: mpTotal || prefill?.total_mandays || 0,
      total_mandays: prefill?.total_mandays || 0,
      labour_breakdown: prefill?.labour_breakdown || [],
      gps,
    };
    const uploadedPhotos = photos.filter(p => p.id).map(p => ({ id: p.id, name: p.name }));
    const offlinePhotos = photos.filter(p => p.offline).map(p => ({ name: p.name, dataUrl: p.dataUrl }));

    const queueOffline = async () => {
      await enqueue('dpr', {
        label: 'DPR',
        payload: { ...basePayload, photos: uploadedPhotos },
        photos: offlinePhotos,
      });
      toast.success('Saved offline — will sync when you reconnect');
      onSaved && onSaved();
    };

    // Explicit offline → queue straight away.
    if (enableOffline && typeof navigator !== 'undefined' && !navigator.onLine) {
      try { await queueOffline(); } catch { toast.error('Could not save offline'); }
      setSubmitting(false);
      return;
    }

    try {
      await apiPost('/dpr', { ...basePayload, photos: uploadedPhotos });
      toast.success('DPR submitted');
      onSaved && onSaved();
    } catch (err) {
      // Online path failed. If it was a network/reachability failure (no server
      // response) and offline mode is enabled, fall back to queueing instead of
      // losing the entry. Genuine validation errors (server responded) surface.
      if (enableOffline && !err?.response) {
        try { await queueOffline(); } catch { toast.error('Could not save offline'); }
      } else {
        toast.error('Failed to submit');
      }
    }
    setSubmitting(false);
  };

  const stepLabels = ['Context', 'Manpower', 'Activities', 'Site Status', 'Safety & QC'];

  return (
    <Wrapper fullscreen={fullscreen}>
      <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">New · Step {step}/5</div>
          <h3 className="font-display font-bold text-lg mt-0.5">Submit DPR</h3>
        </div>
        <button onClick={onClose} data-testid="dpr-close-btn" type="button" className="w-9 h-9 flex items-center justify-center hover:bg-slate-100 rounded-sm"><X size={16} /></button>
      </div>

      <div className="px-2 py-2 bg-slate-50 border-b border-slate-200 flex gap-1 overflow-x-auto">
        {stepLabels.map((s, i) => (
          <button key={s} type="button" onClick={() => setStep(i + 1)} data-testid={`dpr-step-${i+1}`} className={`flex-1 min-w-fit py-1.5 px-2 text-[10px] font-display font-bold uppercase tracking-wider rounded-sm whitespace-nowrap ${step === i + 1 ? 'bg-[#3b4872] text-white' : 'text-slate-500 hover:bg-white'}`}>
            {i + 1}. {s}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="overflow-y-auto flex-1">
        {step === 1 && (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label={`${ff.labelOf('date','Date')}${ff.reqStar('date', true)}`}>
                <input data-testid="dpr-date" type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none" required={ff.isReq('date', true)} />
              </Field>
              <Field label="Report No.">
                <input data-testid="dpr-report-no" value={form.report_no} onChange={e=>setForm({...form,report_no:e.target.value})}
                  placeholder="e.g. DPR-145" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" />
              </Field>
            </div>

            <Field label={`${ff.labelOf('project_id','Project')}${ff.reqStar('project_id', true)}`}>
              <select data-testid="dpr-project-select" value={form.project_id} onChange={e=>setForm({...form,project_id:e.target.value, work_order_id:'', work_order_code:''})}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" required={ff.isReq('project_id', true)}>
                <option value="">— Select project —</option>
                {projects.map(p => <option key={p.id} value={p.id}>{`${p.code} · ${p.name}`}</option>)}
              </select>
            </Field>

            {(() => {
              const wos = projects.find(p => p.id === form.project_id)?.work_orders || [];
              if (wos.length === 0) return null;
              return (
                <Field label="Work Order">
                  <select data-testid="dpr-wo-select" value={form.work_order_id}
                    onChange={e => {
                      const wo = wos.find(w => w.id === e.target.value);
                      setForm({ ...form, work_order_id: e.target.value, work_order_code: wo?.code || '' });
                    }}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white">
                    <option value="">— Project-wide (no WO) —</option>
                    {wos.map(w => <option key={w.id} value={w.id}>{w.code}{w.title ? ` · ${w.title.slice(0, 40)}` : ''}</option>)}
                  </select>
                </Field>
              );
            })()}

            <div className="grid grid-cols-2 gap-3">
              <Field label="Project Manager">
                <input value={form.project_manager} onChange={e=>setForm({...form,project_manager:e.target.value})}
                  placeholder="Name" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" />
              </Field>
              <Field label="Contract Value (₹ Cr)">
                <input type="number" step="0.01" value={form.contract_value_cr} onChange={e=>setForm({...form,contract_value_cr:e.target.value})}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm tabular-nums" />
              </Field>
            </div>

            <Field label="Submitted By">
              <input data-testid="dpr-by" value={form.submitted_by} onChange={e=>setForm({...form,submitted_by:e.target.value})}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" required />
            </Field>

            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 space-y-3" data-testid="dpr-weather">
              <div className="kpi-overline">Weather & Climate</div>
              <div className="grid grid-cols-4 gap-2">
                {WEATHER.map(w => (
                  <button key={w.v} type="button" data-testid={`weather-${w.v.toLowerCase()}`} onClick={()=>setForm({...form,weather:w.v})}
                    className={`py-2.5 border rounded-sm flex flex-col items-center gap-1 text-xs font-display font-semibold ${form.weather===w.v?'border-[#3b4872] bg-indigo-50 text-[#3b4872]':'border-slate-300 text-slate-600 hover:border-slate-400'}`}>
                    <w.icon size={16} />{w.v}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <SmallNumField label="Temp (°C)" v={form.weather_temperature_c} on={v=>setForm({...form,weather_temperature_c:v})} />
                <SmallNumField label="Rainfall (mm)" v={form.weather_rainfall_mm} on={v=>setForm({...form,weather_rainfall_mm:v})} />
                <SmallSelectField label="Wind" v={form.weather_wind} on={v=>setForm({...form,weather_wind:v})} opts={['','Low','Medium','High']} />
                <SmallNumField label="Humidity (%)" v={form.weather_humidity} on={v=>setForm({...form,weather_humidity:v})} />
                <SmallNumField label="Hrs Lost" v={form.weather_impact_hours} on={v=>setForm({...form,weather_impact_hours:Number(v)||0})} />
                <SmallSelectField label="Impact on Work" v={form.weather_impact_flag} on={v=>setForm({...form,weather_impact_flag:v})} opts={['No','Yes']} />
              </div>
              <input value={form.weather_remarks} onChange={e=>setForm({...form,weather_remarks:e.target.value})}
                placeholder="Weather remarks (optional)" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-xs" />
            </div>

            <Field label="GPS Location">
              <div className="flex gap-2">
                <input value={gps} readOnly placeholder="Tap GPS" className="flex-1 px-3 py-2.5 border border-slate-300 rounded-sm text-sm font-mono bg-slate-50" />
                <button type="button" onClick={getGps} className="px-3 py-2.5 bg-[#3b4872] text-white hover:bg-[#2d3856] text-xs font-display font-semibold rounded-sm hover:bg-[#3b4872] flex items-center gap-1"><MapPin size={13} /></button>
              </div>
            </Field>
          </div>
        )}

        {step === 2 && (
          <div className="p-4 space-y-4">
            <div className="bg-white border border-slate-200 rounded-sm p-3" data-testid="dpr-manpower-count">
              <div className="flex items-center justify-between mb-2">
                <div className="kpi-overline flex items-center gap-1.5"><Users size={11} /> Manpower Status (Staff & Labour)</div>
                <div className="text-[10px] text-slate-500">Total: <b className="text-[#0F172A] tabular-nums">{mpTotal}</b></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {MANPOWER_CATEGORIES.map(c => (
                  <div key={c.k}>
                    <label className="text-[9px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">{c.label}</label>
                    <input type="number" min="0" value={form.manpower_count[c.k] || 0}
                      onChange={e => setMpCount(c.k, e.target.value)}
                      data-testid={`dpr-mp-${c.k}`}
                      className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-sm tabular-nums focus:border-[#3b4872] focus:outline-none" />
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-sm p-3" data-testid="dpr-manpower-perf">
              <div className="kpi-overline flex items-center gap-1.5 mb-2"><Hammer size={11} /> Performance of Staff & Labour</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {MANPOWER_CATEGORIES.map(c => (
                  <div key={c.k}>
                    <label className="text-[9px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">{c.label}</label>
                    <select value={form.manpower_performance[c.k] || ''} onChange={e => setMpPerf(c.k, e.target.value)}
                      className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white">
                      {PERF_OPTS.map(o => <option key={o} value={o === '—' ? '' : o}>{o}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="dpr-labour-category">
              <div className="flex items-center justify-between mb-2">
                <div className="kpi-overline flex items-center gap-1.5"><Users size={11} /> Labour Category Split (vs Attendance)</div>
                <div className="text-[10px] text-slate-500">
                  Attendance total: <b className="text-[#0F172A] tabular-nums" data-testid="dpr-attendance-total">{attendanceTotal}</b>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { k: 'skilled', label: 'Skilled' },
                  { k: 'semi_skilled', label: 'Semi-skilled' },
                  { k: 'unskilled', label: 'Unskilled' },
                  { k: 'supervisor', label: 'Supervisor/Eng' },
                ].map(({ k, label }) => (
                  <div key={k}>
                    <label className="text-[9px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">{label}</label>
                    <input type="number" min="0" value={form.labour_category_split[k] || 0}
                      onChange={e => setCat(k, e.target.value)}
                      data-testid={`dpr-cat-${k}`}
                      className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-sm tabular-nums focus:border-[#3b4872] focus:outline-none" />
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-center justify-between text-[11px]">
                <span>Sum (Skilled+Semi+Unskilled): <b className="font-display font-bold tabular-nums" data-testid="dpr-cat-total">{catTotal}</b></span>
                <span className={`font-display font-bold ${variance === 0 ? 'text-emerald-700' : Math.abs(variance) <= 2 ? 'text-amber-700' : 'text-rose-700'}`} data-testid="dpr-cat-variance">
                  Variance: {variance > 0 ? '+' : ''}{variance}
                  {variance === 0 ? ' · ✓ matches' : (attendanceTotal === 0 ? ' (no attendance)' : variance > 0 ? ' over' : ' under')}
                </span>
              </div>
            </div>

            {prefill && (
              <div className="bg-indigo-50/40 border border-indigo-200 rounded-sm p-3 text-xs space-y-1">
                <div className="kpi-overline">Auto-pulled · {form.date}</div>
                <div className="flex items-center gap-2"><Users size={12} className="text-[#3b4872]" /> <b className="tabular-nums">{prefill.total_mandays}</b> mandays from {prefill.labour_breakdown.length} subcontractor(s)</div>
                {prefill.labour_breakdown.slice(0, 3).map((l, i) => (
                  <div key={i} className="ml-5 text-[11px] text-slate-600">• {l.subcontractor_name} ({l.trade}) · P {l.present} / H {l.half_day} / A {l.absent}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="p-4 space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="kpi-overline flex items-center gap-1.5"><ClipboardList size={11} /> BOQ-linked Activities Done Today</div>
                <button type="button" onClick={addActivity} data-testid="dpr-add-activity" className="text-[#3b4872] text-xs font-display font-semibold flex items-center gap-1"><Plus size={11} /> Add</button>
              </div>
              {form.activities.length === 0 && <div className="text-[11px] text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-sm">Click "+ Add" to log activities. Push to BOQ later via "Push to BOQ" button.</div>}
              <div className="space-y-2">
                {form.activities.map((a, i) => (
                  <div key={i} className="bg-slate-50 border border-slate-200 rounded-sm p-2.5 space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <select value={a.boq_id} onChange={e => pickBoq(i, e.target.value)} className="flex-1 px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white">
                        <option value="">— Link BOQ item (optional) —</option>
                        {boqItems.slice(0, 200).map(b => <option key={b.id} value={b.id}>{b.item_code} · {b.description?.slice(0, 60)}</option>)}
                      </select>
                      <button type="button" onClick={() => removeActivity(i)} className="text-slate-400 hover:text-red-500"><X size={12} /></button>
                    </div>
                    <input value={a.activity} onChange={e => updateActivity(i, 'activity', e.target.value)} placeholder="Activity description" className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-xs" />
                    <div className="grid grid-cols-3 gap-1.5">
                      <input type="number" step="any" value={a.qty_done} onChange={e => updateActivity(i, 'qty_done', Number(e.target.value))} placeholder="Qty done" className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs tabular-nums" />
                      <input value={a.unit} onChange={e => updateActivity(i, 'unit', e.target.value)} placeholder="Unit" className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs" />
                      <input value={a.remarks} onChange={e => updateActivity(i, 'remarks', e.target.value)} placeholder="Remarks" className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Field label="Activity Description (narrative)">
              <textarea data-testid="dpr-activity-desc" value={form.activity_description} onChange={e=>setForm({...form,activity_description:e.target.value})} rows={3}
                placeholder="Pier P-23 to P-25 reinforcement complete. Concrete pour scheduled tomorrow." className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" />
            </Field>

            <RepeatingTable testId="dpr-store-consumption" title="Store & Material Consumption (Today)" icon={Boxes}
              rows={form.store_consumption}
              onAdd={() => arrAdd('store_consumption', { rev: '', material: '', spec: '', unit: '', opening: 0, received: 0, issued: 0, remarks: '' })}
              onRemove={(i) => arrRemove('store_consumption', i)}
              update={(i, k, v) => arrUpdate('store_consumption', i, k, v)}
              cols={[
                { k: 'rev', label: 'Rev', w: 'w-16', type: 'text' },
                { k: 'material', label: 'Material Description', w: 'flex-1', type: 'text' },
                { k: 'spec', label: 'Specification', w: 'w-32', type: 'text' },
                { k: 'unit', label: 'Unit', w: 'w-20', type: 'text' },
                { k: 'opening', label: 'Opening', w: 'w-20', type: 'number' },
                { k: 'received', label: 'Received', w: 'w-20', type: 'number' },
                { k: 'issued', label: 'Issued', w: 'w-20', type: 'number' },
                { k: 'remarks', label: 'Remarks', w: 'flex-1', type: 'text' },
              ]}
              extraCol={(row) => (
                <div className="w-20 text-xs tabular-nums text-slate-700">
                  Close: <b>{(Number(row.opening) || 0) + (Number(row.received) || 0) - (Number(row.issued) || 0)}</b>
                </div>
              )}
            />

            <RepeatingTable testId="dpr-procurement-next" title="Procurement & Material Requirement (Next Day)" icon={Truck}
              rows={form.procurement_next_day}
              onAdd={() => arrAdd('procurement_next_day', { drawing_no: '', material: '', spec: '', unit: '', qty_required: 0, available_stock: 0, qty_to_procure: 0, vendor: '', remarks: '' })}
              onRemove={(i) => arrRemove('procurement_next_day', i)}
              update={(i, k, v) => arrUpdate('procurement_next_day', i, k, v)}
              cols={[
                { k: 'drawing_no', label: 'Drawing No', w: 'w-24', type: 'text' },
                { k: 'material', label: 'Material', w: 'flex-1', type: 'text' },
                { k: 'spec', label: 'Spec/Grade', w: 'w-28', type: 'text' },
                { k: 'unit', label: 'Unit', w: 'w-20', type: 'text' },
                { k: 'qty_required', label: 'Req (Next Day)', w: 'w-24', type: 'number' },
                { k: 'available_stock', label: 'In Stock', w: 'w-20', type: 'number' },
                { k: 'qty_to_procure', label: 'To Procure', w: 'w-24', type: 'number' },
                { k: 'vendor', label: 'Vendor / Source', w: 'w-32', type: 'text' },
                { k: 'remarks', label: 'Remarks', w: 'flex-1', type: 'text' },
              ]}
            />

            <RepeatingTable testId="dpr-boq-consumption" title="Material Consumption vs BOQ Quantity" icon={ClipboardList}
              rows={form.boq_consumption}
              onAdd={() => arrAdd('boq_consumption', { material: '', unit: '', boq_qty: 0, cumulative_consumed: 0, remarks: '' })}
              onRemove={(i) => arrRemove('boq_consumption', i)}
              update={(i, k, v) => arrUpdate('boq_consumption', i, k, v)}
              cols={[
                { k: 'material', label: 'Material', w: 'flex-1', type: 'text' },
                { k: 'unit', label: 'Unit', w: 'w-20', type: 'text' },
                { k: 'boq_qty', label: 'BOQ Qty', w: 'w-24', type: 'number' },
                { k: 'cumulative_consumed', label: 'Cum Consumed', w: 'w-28', type: 'number' },
                { k: 'remarks', label: 'Remarks', w: 'flex-1', type: 'text' },
              ]}
              extraCol={(row) => {
                const boq = Number(row.boq_qty) || 0;
                const cum = Number(row.cumulative_consumed) || 0;
                const balance = boq - cum;
                const pct = boq > 0 ? Math.round((cum / boq) * 100) : 0;
                return (
                  <div className="w-32 text-[11px] tabular-nums">
                    Bal: <b className={balance < 0 ? 'text-rose-700' : ''}>{balance}</b> · {pct}%
                  </div>
                );
              }}
            />

            <RepeatingTable testId="dpr-materials-consumed" title="Materials Consumed (Quick Log)" icon={Boxes}
              rows={form.materials_consumed}
              onAdd={() => arrAdd('materials_consumed', { material: '', qty: 0, unit: '' })}
              onRemove={(i) => arrRemove('materials_consumed', i)}
              update={(i, k, v) => arrUpdate('materials_consumed', i, k, v)}
              cols={[
                { k: 'material', label: 'Material', w: 'flex-1', type: 'text' },
                { k: 'qty', label: 'Qty', w: 'w-24', type: 'number' },
                { k: 'unit', label: 'Unit', w: 'w-24', type: 'text' },
              ]}
            />
          </div>
        )}

        {step === 4 && (
          <div className="p-4 space-y-4">
            <RepeatingTable testId="dpr-work-fronts" title="Work Front Available Status" icon={ClipboardList}
              rows={form.work_fronts}
              onAdd={() => arrAdd('work_fronts', { area: '', activity: '', available: 'Yes', date_available_from: '', dependency: '', reason: '', expected_date: '', remarks: '' })}
              onRemove={(i) => arrRemove('work_fronts', i)}
              update={(i, k, v) => arrUpdate('work_fronts', i, k, v)}
              cols={[
                { k: 'area', label: 'Area / Location', w: 'flex-1', type: 'text' },
                { k: 'activity', label: 'Activity', w: 'flex-1', type: 'text' },
                { k: 'available', label: 'Available', w: 'w-24', type: 'select', opts: ['Yes', 'No', 'Partial'] },
                { k: 'date_available_from', label: 'From', w: 'w-32', type: 'date' },
                { k: 'dependency', label: 'Dependency', w: 'flex-1', type: 'text' },
                { k: 'reason', label: 'Reason if N/A', w: 'flex-1', type: 'text' },
                { k: 'expected_date', label: 'Expected', w: 'w-32', type: 'date' },
                { k: 'remarks', label: 'Remarks', w: 'flex-1', type: 'text' },
              ]}
            />

            <RepeatingTable testId="dpr-work-holds" title="Work Hold / Stoppage from Client" icon={Pause}
              rows={form.work_holds}
              onAdd={() => arrAdd('work_holds', { ref: '', client: '', reason: '', from_date: '', to_date: '', remarks: '' })}
              onRemove={(i) => arrRemove('work_holds', i)}
              update={(i, k, v) => arrUpdate('work_holds', i, k, v)}
              cols={[
                { k: 'ref', label: 'Hold Ref / Email', w: 'flex-1', type: 'text' },
                { k: 'client', label: 'Client', w: 'flex-1', type: 'text' },
                { k: 'reason', label: 'Reason', w: 'flex-1', type: 'text' },
                { k: 'from_date', label: 'From', w: 'w-32', type: 'date' },
                { k: 'to_date', label: 'To', w: 'w-32', type: 'date' },
                { k: 'remarks', label: 'Remarks', w: 'flex-1', type: 'text' },
              ]}
            />

            <RepeatingTable testId="dpr-rework" title="Rework Details (Client / Site Mistake)" icon={FileWarning}
              rows={form.rework_items}
              onAdd={() => arrAdd('rework_items', { location: '', reason: '', responsibility: 'Contractor', qty: 0, cost_impact: 0, time_impact_days: 0, approval_ref: '', remarks: '' })}
              onRemove={(i) => arrRemove('rework_items', i)}
              update={(i, k, v) => arrUpdate('rework_items', i, k, v)}
              cols={[
                { k: 'location', label: 'Activity / Location', w: 'flex-1', type: 'text' },
                { k: 'reason', label: 'Reason', w: 'flex-1', type: 'text' },
                { k: 'responsibility', label: 'Responsibility', w: 'w-32', type: 'select', opts: ['Client', 'Contractor', 'Designer', 'Other'] },
                { k: 'qty', label: 'Qty', w: 'w-20', type: 'number' },
                { k: 'cost_impact', label: 'Cost (₹)', w: 'w-24', type: 'number' },
                { k: 'time_impact_days', label: 'Days', w: 'w-20', type: 'number' },
                { k: 'approval_ref', label: 'Approval Ref', w: 'flex-1', type: 'text' },
                { k: 'remarks', label: 'Remarks', w: 'flex-1', type: 'text' },
              ]}
            />

            <div data-testid="dpr-hindrance-section">
              <div className="flex items-center justify-between mb-2">
                <div className="kpi-overline flex items-center gap-1.5"><AlertTriangle size={11} /> Hindrance Log</div>
                <button type="button" onClick={() => arrAdd('hindrances', { description: '', impact_hours: 0, category: 'Other', remarks: '' })} data-testid="dpr-add-hindrance" className="text-[#3b4872] text-xs font-display font-semibold flex items-center gap-1"><Plus size={11} /> Add</button>
              </div>
              {(form.hindrances || []).length === 0 && <div className="text-[11px] text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-sm">No hindrances logged. Click "+ Add" to log delays, work stoppages, or blockers.</div>}
              <div className="space-y-2">
                {(form.hindrances || []).map((h, i) => (
                  <div key={i} className="bg-amber-50 border border-amber-200 rounded-sm p-2.5 space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <select value={h.category} onChange={e => arrUpdate('hindrances', i, 'category', e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white">
                        {['Weather','Material','Manpower','Equipment','Design/RFI','Client','Statutory','Other'].map(c => <option key={c}>{c}</option>)}
                      </select>
                      <input type="number" step="0.5" min="0" max="24" value={h.impact_hours || 0} onChange={e => arrUpdate('hindrances', i, 'impact_hours', Number(e.target.value))} placeholder="Hrs lost" className="w-24 px-2 py-1.5 border border-slate-300 rounded-sm text-xs tabular-nums" />
                      <button type="button" onClick={() => arrRemove('hindrances', i)} className="text-slate-400 hover:text-red-500 ml-auto"><X size={12} /></button>
                    </div>
                    <input value={h.description} onChange={e => arrUpdate('hindrances', i, 'description', e.target.value)} placeholder="Description" data-testid={`dpr-hindrance-desc-${i}`} className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-xs" />
                    <input value={h.remarks || ''} onChange={e => arrUpdate('hindrances', i, 'remarks', e.target.value)} placeholder="Action taken / Resolution plan" className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-xs" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="p-4 space-y-4">
            <RepeatingTable testId="dpr-safety" title="Safety Status (Daily)" icon={ShieldCheck}
              rows={form.safety_checks}
              onAdd={() => arrAdd('safety_checks', { parameter: '', status: '', details: '', manhours_today: 0, total_manhours: 0, incident: '', action: '', remarks: '' })}
              onRemove={(i) => arrRemove('safety_checks', i)}
              update={(i, k, v) => arrUpdate('safety_checks', i, k, v)}
              cols={[
                { k: 'parameter', label: 'Parameter', w: 'flex-1', type: 'text' },
                { k: 'status', label: 'Status', w: 'w-24', type: 'select', opts: ['', 'Yes', 'No', 'N/A'] },
                { k: 'details', label: 'Details', w: 'flex-1', type: 'text' },
                { k: 'manhours_today', label: 'M-hrs Today', w: 'w-24', type: 'number' },
                { k: 'total_manhours', label: 'M-hrs Total', w: 'w-24', type: 'number' },
                { k: 'incident', label: 'Incident / Near Miss', w: 'flex-1', type: 'text' },
                { k: 'action', label: 'Action Taken', w: 'flex-1', type: 'text' },
                { k: 'remarks', label: 'Remarks', w: 'flex-1', type: 'text' },
              ]}
            />

            <RepeatingTable testId="dpr-qc" title="Quality Control Status (Daily)" icon={ClipboardList}
              rows={form.qc_checks}
              onAdd={() => arrAdd('qc_checks', { activity: '', status: '', remarks: '' })}
              onRemove={(i) => arrRemove('qc_checks', i)}
              update={(i, k, v) => arrUpdate('qc_checks', i, k, v)}
              cols={[
                { k: 'activity', label: 'Quality Activity', w: 'flex-1', type: 'text' },
                { k: 'status', label: 'Status', w: 'w-32', type: 'select', opts: ['', 'Approved', 'Pending', 'Rejected', 'N/A'] },
                { k: 'remarks', label: 'Remarks', w: 'flex-1', type: 'text' },
              ]}
            />

            <RepeatingTable testId="dpr-housekeeping" title="Material Stacking & Housekeeping Status" icon={Boxes}
              rows={form.housekeeping_checks}
              onAdd={() => arrAdd('housekeeping_checks', { area: '', stacking: 'Proper', housekeeping: 'Good', waste_disposed: 'Yes', hazard_observed: 'No', corrective_action: '', responsible: '', remarks: '' })}
              onRemove={(i) => arrRemove('housekeeping_checks', i)}
              update={(i, k, v) => arrUpdate('housekeeping_checks', i, k, v)}
              cols={[
                { k: 'area', label: 'Area / Location', w: 'flex-1', type: 'text' },
                { k: 'stacking', label: 'Stacking', w: 'w-28', type: 'select', opts: ['Proper', 'Improper'] },
                { k: 'housekeeping', label: 'Housekeeping', w: 'w-24', type: 'select', opts: ['Good', 'Avg', 'Poor'] },
                { k: 'waste_disposed', label: 'Waste Disposed', w: 'w-28', type: 'select', opts: ['Yes', 'No'] },
                { k: 'hazard_observed', label: 'Hazard', w: 'w-20', type: 'select', opts: ['No', 'Yes'] },
                { k: 'corrective_action', label: 'Corrective Action', w: 'flex-1', type: 'text' },
                { k: 'responsible', label: 'Responsible', w: 'w-32', type: 'text' },
                { k: 'remarks', label: 'Remarks', w: 'flex-1', type: 'text' },
              ]}
            />

            <RepeatingTable testId="dpr-plant-machinery" title="Plant & Machinery Status (Daily)" icon={Truck}
              rows={form.plant_machinery}
              onAdd={() => arrAdd('plant_machinery', { equipment: '', make: '', required: 0, deployed: 0, working_hours: 0, idle_hours: 0, fuel: '', hire_cost_hr: 0, remarks: '' })}
              onRemove={(i) => arrRemove('plant_machinery', i)}
              update={(i, k, v) => arrUpdate('plant_machinery', i, k, v)}
              cols={[
                { k: 'equipment', label: 'Equipment', w: 'flex-1', type: 'text' },
                { k: 'make', label: 'Make / Capacity', w: 'flex-1', type: 'text' },
                { k: 'required', label: 'Req', w: 'w-16', type: 'number' },
                { k: 'deployed', label: 'Dep', w: 'w-16', type: 'number' },
                { k: 'working_hours', label: 'Working Hrs', w: 'w-24', type: 'number' },
                { k: 'idle_hours', label: 'Idle / Breakdown Hrs', w: 'w-28', type: 'number' },
                { k: 'fuel', label: 'Fuel / Power', w: 'w-28', type: 'text' },
                { k: 'hire_cost_hr', label: 'Hire ₹/Hr', w: 'w-24', type: 'number' },
                { k: 'remarks', label: 'Remarks', w: 'flex-1', type: 'text' },
              ]}
              extraCol={(row) => {
                const idleCost = (Number(row.idle_hours) || 0) * (Number(row.hire_cost_hr) || 0);
                return <div className="w-24 text-[11px] tabular-nums text-rose-700">Idle ₹{idleCost.toFixed(0)}</div>;
              }}
            />

            <Field label="Issues / Observations">
              <textarea value={form.issues||''} onChange={e=>setForm({...form,issues:e.target.value})} rows={2} placeholder="None" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" />
            </Field>

            <Field label="Safety Incidents (free text — supplements table above)">
              <textarea value={form.safety_incidents||''} onChange={e=>setForm({...form,safety_incidents:e.target.value})} rows={2} placeholder="None" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" />
            </Field>

            <Field label="RFIs Raised Today">
              <input value={form.rfis_raised||''} onChange={e=>setForm({...form,rfis_raised:e.target.value})} placeholder="RFI numbers (comma separated)" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" />
            </Field>

            <Field label="Site Photos">
              <label className="block border-2 border-dashed border-slate-300 rounded-sm p-5 text-center cursor-pointer hover:border-[#3b4872] hover:bg-indigo-50/30">
                <input data-testid="dpr-photo-input" type="file" accept="image/*" multiple capture="environment" onChange={onUpload} className="hidden" />
                <Camera size={22} className="mx-auto text-slate-400 mb-2" />
                <div className="text-sm font-display font-semibold">{uploading ? 'Uploading…' : 'Take or upload photos'}</div>
              </label>
              {photos.length > 0 && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  {photos.map((p, i) => (
                    <div key={p.id || `offline-${i}`} className="relative group">
                      <PhotoThumb fileId={p.id} dataUrl={p.dataUrl} />
                      {p.offline && (
                        <span className="absolute bottom-0 inset-x-0 bg-amber-500/90 text-white text-[8px] font-display font-bold uppercase tracking-wider text-center py-0.5">Offline</span>
                      )}
                      <button type="button" onClick={()=>setPhotos(ph => ph.filter((_, idx) => idx !== i))} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-sm flex items-center justify-center opacity-0 group-hover:opacity-100"><X size={11} /></button>
                    </div>
                  ))}
                </div>
              )}
            </Field>

            <Field label="Supervisor Sign-off">
              <input value={form.supervisor_signoff||''} onChange={e=>setForm({...form,supervisor_signoff:e.target.value})} placeholder="Supervisor name (initials/digital sign-off)" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" />
            </Field>
          </div>
        )}

        <div className="flex justify-between gap-2 pt-3 border-t border-slate-200 sticky bottom-0 bg-white p-3" style={{ paddingBottom: fullscreen ? 'calc(0.75rem + env(safe-area-inset-bottom))' : '0.75rem' }}>
          {step > 1 ? <button type="button" onClick={() => setStep(step - 1)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm font-display">← Back</button> : <span />}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
            {step < 5 ? (
              <button type="button" onClick={() => setStep(step + 1)} data-testid="dpr-next-btn" className="px-5 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold">Next →</button>
            ) : (
              <button data-testid="dpr-submit-btn" type="submit" disabled={submitting} className="px-5 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold disabled:opacity-50">
                {submitting ? 'Submitting…' : 'Submit DPR'}
              </button>
            )}
          </div>
        </div>
      </form>
    </Wrapper>
  );
};

// Module-scope (not re-declared per render) — a component redeclared inside its
// parent's render body gets a new function identity on every render, so React
// unmounts/remounts the whole subtree on every keystroke instead of diffing it.
const Wrapper = ({ children, fullscreen }) => fullscreen ? (
  <div className="bg-white min-h-screen flex flex-col">{children}</div>
) : (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center md:p-4">
    <div onClick={e=>e.stopPropagation()} className="bg-white w-full md:max-w-3xl md:rounded-sm md:max-h-[92vh] flex flex-col">
      {children}
    </div>
  </div>
);


const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">{label}</label>
    {children}
  </div>
);

const SmallNumField = ({ label, v, on }) => (
  <div>
    <label className="text-[9px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">{label}</label>
    <input type="number" step="any" value={v} onChange={e=>on(e.target.value)} className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-xs tabular-nums focus:border-[#3b4872] focus:outline-none" />
  </div>
);

const SmallSelectField = ({ label, v, on, opts }) => (
  <div>
    <label className="text-[9px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">{label}</label>
    <select value={v} onChange={e=>on(e.target.value)} className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white focus:border-[#3b4872] focus:outline-none">
      {opts.map(o => <option key={o} value={o}>{o || '—'}</option>)}
    </select>
  </div>
);

const RepeatingTable = ({ testId, title, icon: Icon, rows, cols, onAdd, onRemove, update, extraCol }) => (
  <div data-testid={testId}>
    <div className="flex items-center justify-between mb-2">
      <div className="kpi-overline flex items-center gap-1.5">{Icon && <Icon size={11} />} {title}</div>
      <button type="button" onClick={onAdd} data-testid={`${testId}-add`} className="text-[#3b4872] text-xs font-display font-semibold flex items-center gap-1"><Plus size={11} /> Add</button>
    </div>
    {(rows || []).length === 0 && (
      <div className="text-[11px] text-slate-400 py-3 text-center border border-dashed border-slate-200 rounded-sm">No rows — click "+ Add" to log.</div>
    )}
    {(rows || []).length > 0 && (
      <div className="overflow-x-auto">
        <div className="space-y-1.5 min-w-fit">
          <div className="flex gap-1.5 text-[9px] font-display font-bold uppercase tracking-widest text-slate-500 px-1">
            {cols.map(c => (<div key={c.k} className={c.w}>{c.label}</div>))}
            {extraCol && <div className="w-24" />}
            <div className="w-6" />
          </div>
          {(rows || []).map((r, i) => (
            <div key={i} className="flex gap-1.5 items-center">
              {cols.map(c => {
                const cls = `${c.w} px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white tabular-nums focus:border-[#3b4872] focus:outline-none`;
                if (c.type === 'select') {
                  return (
                    <select key={c.k} value={r[c.k] || ''} onChange={e => update(i, c.k, e.target.value)} className={cls}>
                      {c.opts.map(o => <option key={o} value={o}>{o || '—'}</option>)}
                    </select>
                  );
                }
                return (
                  <input
                    key={c.k}
                    type={c.type === 'number' ? 'number' : c.type === 'date' ? 'date' : 'text'}
                    step={c.type === 'number' ? 'any' : undefined}
                    value={r[c.k] ?? ''}
                    onChange={e => update(i, c.k, c.type === 'number' ? Number(e.target.value) : e.target.value)}
                    placeholder={c.label}
                    className={cls}
                  />
                );
              })}
              {extraCol && extraCol(r)}
              <button type="button" onClick={() => onRemove(i)} className="text-slate-400 hover:text-red-500 w-6 flex-shrink-0"><X size={12} /></button>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

const PhotoThumb = ({ fileId, dataUrl }) => {
  const [url, setUrl] = useState(dataUrl || null);
  useEffect(() => {
    if (dataUrl) { setUrl(dataUrl); return; }
    if (!fileId) return;
    let active = true;
    api.get(`/files/${fileId}/download`, { responseType: 'blob' })
      .then(r => { if (active) setUrl(URL.createObjectURL(r.data)); })
      .catch(()=>{});
    return () => { active = false; if (url && !dataUrl) URL.revokeObjectURL(url); };
    // eslint-disable-next-line
  }, [fileId, dataUrl]);
  return (
    <div className="w-20 h-20 bg-slate-100 rounded-sm overflow-hidden border border-slate-200 shrink-0">
      {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-400"><Camera size={16} /></div>}
    </div>
  );
};

/** Compress an image File to a JPEG data URL (~70% quality, max 1280px edge)
 *  so offline photos stay small (200–400 KB) in IndexedDB. */
function compressImage(file, maxEdge = 1280, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read failed'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('decode failed'));
      img.onload = () => {
        let { width, height } = img;
        if (width > maxEdge || height > maxEdge) {
          if (width >= height) { height = Math.round(height * (maxEdge / width)); width = maxEdge; }
          else { width = Math.round(width * (maxEdge / height)); height = maxEdge; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
